import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { OrderUpdateAction } from 'src/app/model/interfaces/order-update-action';
import { NavBarService, ResourceService } from 'src/app/services';
import { Order } from '../../../model/interfaces/order';
import { OrderService } from '../../../services/order.service';

@Component({
    selector: 'app-basket-shell',
    templateUrl: './basket-shell.component.html',
    styleUrls: ['./basket-shell.component.scss']
})
export class BasketShellComponent implements OnInit, OnDestroy
{
    private readonly orderService: OrderService;
    private readonly navBarService: NavBarService;
    private readonly resourceService: ResourceService;
    private orderUpdatedSubscription: Subscription;
    private navbarBasketIconClickSubscription: Subscription;
    private orderExpiresNotificationSubscription: Subscription;
    private orderExpiredSubscription: Subscription;
    private orderUpdatePendingSubscription: Subscription;
    private orderFullSubscription: Subscription;
    private pendingUpdateActiveHandle = null;
    private changeDetector: ChangeDetectorRef;
    private updatePendingSince = new Date();

    public basketNotify: boolean;
    public notifyDelete: boolean;
    public expanded: boolean;
    public itemCount = 0;
    public itemName: string;
    public addedItemName: string;
    public addedItemType: string;
    public addedItemNormalizedName: string;
    public updatePending = false;
    public pendingUpdateActive = false;
    public pendingUpdates = 0;

    @Input('navigate-checkout')
    public navigateCheckout = true;

    @Input('toast')
    public toast = true;

    public constructor(
        changeDetector: ChangeDetectorRef,
        navBarService: NavBarService,
        orderService: OrderService,
        resourceService: ResourceService)
    {
        this.changeDetector = changeDetector;
        this.orderService = orderService;
        this.navBarService = navBarService;
        this.resourceService = resourceService;
    }

    public async ngOnInit(): Promise<void>
    {
        this.setUpSubscriptions();
        const order = await this.orderService.getOrder(false);
        this.setItemCount(order);
    }

    private setUpSubscriptions(): void
    {
        this.orderUpdatedSubscription = this.orderService.onOrderContentUpdated.subscribe((updateInfo: { order: Order, action: OrderUpdateAction }) =>
        {
            this.orderUpdated(updateInfo.order, updateInfo.action);
        });

        this.navbarBasketIconClickSubscription = this.navBarService.menuClick.subscribe(
            (menuItem: { name: string, route: string }) =>
            {
                if (menuItem.name === 'basket')
                {
                    this.toggleExpanded();
                }
            }
        );

        if(this.toast)
        {
            if(this.navBarService.showBasket)
            {
                this.toggleExpanded();
                this.navBarService.showBasketFlag(false);
            }
        }

        this.orderExpiresNotificationSubscription = this.orderService.onOrderExpirationNotice.subscribe((order: Order) =>
        {
          // TODO: Should this trigger a notification to the user in a modal.
            console.warn('Order expires in a short while');
        });

        this.orderExpiredSubscription = this.orderService.onOrderExpired.subscribe((order: Order) =>
        {
            this.terminate();
        });

        this.orderUpdatePendingSubscription = this.orderService.onOrderUpdateIntent.subscribe(() => this.setUpdatePending());
        this.orderFullSubscription = this.orderService.onOrderFull.subscribe(() => this.orderFull());
    }

    private setUpdatePending(): void
    {
        window.clearTimeout(this.pendingUpdateActiveHandle);
        this.pendingUpdateActive = true;

        setTimeout(() =>
        {
            this.updatePendingSince = new Date();
            this.pendingUpdates++;
            this.changeDetector.detectChanges();
        }, 0);
    }

    private setItem(action: OrderUpdateAction): void
    {
        if (action.itemType === 'ticketpackage')
        {
            this.addedItemName = action.item['Name'];
            this.addedItemType = 'package';
            this.addedItemNormalizedName = this.addedItemName;
        }
        else if (action.itemType === 'sharetype')
        {
            const name = action.item['Name'];
            this.addedItemName = this.resourceService.getString('global_share_type_name_' + name);
            this.addedItemType = 'ticket';
            this.addedItemNormalizedName = this.resourceService.getString('global_ticket_normalized_name_' + action.item['Size']);
        }
        else if (action.itemType === 'share')
        {
            const name = action.item['Type']['Name'];
            this.addedItemName = this.resourceService.getString('global_share_type_name_' + name);
            this.addedItemType = 'ticket';
            this.addedItemNormalizedName = this.resourceService.getString('global_ticket_normalized_name_' + action.item['Type']['Size']);
        }
    }

    private orderUpdated(order: Order, action: OrderUpdateAction): void
    {
        const actionType = action ? action.actionType : 'unknown';

        // Filter that makes sure we only process the event when something is deleted from or added to the order.
        if (!(actionType === 'add' || actionType === 'delete'))
        {
            return;
        }

        this.setItem(action);
        this.setItemCount(order);

        if (this.itemCount > 0)
        {
            this.setItemCount(order);

            if (actionType === 'add')
            {
                this.itemAdded(action);
            } else if (action.actionType === 'delete')
            {
                this.itemDeleted();
            }
        } else if (this.itemCount === 0)
        {
            this.expanded = false;
        }

        this.decrementPendingUpdate();
    }

    private orderFull(): void
    {
        this.decrementPendingUpdate();
    }

    private decrementPendingUpdate(): void
    {
        const delay = Math.max(0, 1000 - (+new Date() - +this.updatePendingSince));

        setTimeout(() =>
        {
            // Safe check to avoid negative values caused by unforeseen events.
            this.pendingUpdates = Math.max(0, this.pendingUpdates - 1);
            this.pendingUpdateActiveHandle = setTimeout(() =>
            {
                this.pendingUpdateActive = false;
                this.changeDetector.detectChanges();
            }, 1000);
        }, delay);
    }

    private itemAdded(action: OrderUpdateAction): void
    {
        const item = action.item;
        this.basketNotify = true;

        setTimeout(() =>
        {
            this.basketNotify = false;
        }, this.itemCount > 1 ? 2000 : 3000);
    }

    private itemDeleted(): void
    {
        this.notifyDelete = true;
        setTimeout(() =>
        {
            this.notifyDelete = false;
        }, 1000);
    }

    public toggleExpanded(): void
    {
        this.expanded = !this.expanded;
    }

    public onShellClick(event: MouseEvent): void
    {
        this.expanded = false;
    }

    public onContainerClick(event: MouseEvent): void
    {
        event.preventDefault();
        event.stopPropagation();
    }

    private terminate(): void
    {
        if (this.expanded)
        {
            this.expanded = false;
        }

        this.setItemCount(null);
    }

    private setItemCount(order: Order | null): void
    {
        if (order == null)
        {
            this.itemCount = 0;
            this.itemName = 'Kurven er tom';
            return;
        }

        const packageCount = order.TicketPackageInstances.length;
        const shareCount = order.Shares.length;

        const itemCount = order != null ? order.Shares.length + order.TicketPackageInstances.length : 0;
        this.itemCount = itemCount;

        if (packageCount === 0 && shareCount > 0)
        {
            this.itemName = this.itemCount === 1 ? 'Lod' : 'Lodder';
        }
        else if (shareCount === 0 && packageCount > 0)
        {
            this.itemName = this.itemCount === 1 ? 'Lodpakke' : 'Lodpakker';
        }
        else
        {
            this.itemName = 'Produkter';
        }
    }

    public ngOnDestroy(): void
    {
        clearTimeout(this.pendingUpdateActiveHandle);
        this.orderUpdatedSubscription.unsubscribe();
        this.navbarBasketIconClickSubscription.unsubscribe();
        this.orderExpiresNotificationSubscription.unsubscribe();
        this.orderExpiredSubscription.unsubscribe();
        this.orderUpdatePendingSubscription.unsubscribe();
        this.orderFullSubscription.unsubscribe();
    }
}
